import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { StringListFields } from '@/components/ui/StringListFields';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  type FormErrors,
} from '@/lib/form-errors';
import type { Product } from '@/types/products';
import { FaqFields } from './FaqFields';
import { OrderVariantFields } from './OrderVariantFields';
import { PriceTierFields } from './PriceTierFields';
import { ProductPhotoFields } from './ProductPhotoFields';
import { VariantPhotoFields } from './VariantPhotoFields';
import {
  MAX_COMBINACIONES,
  PRODUCT_FORM_FIELDS,
  combinacionesVariantes,
  createProductPayload,
  ejesConOpciones,
  emptyProductForm,
  hayStockPorCombinacion,
  productFormStateFrom,
  todosLosEjesSonDeTalla,
  updateProductPayload,
  type ProductFormState,
} from './products-payload';
import { PRODUCT_LIMITS, validateProductForm } from './products-validation';
import { VariantInventoryFields } from './VariantInventoryFields';
import { useCreateProduct, useUpdateProduct } from './use-products';

/**
 * Alta y edición de un producto (`POST` / `PATCH .../products`, solo ADMIN).
 *
 * Es el formulario más largo del panel porque el producto entero es la fuente del prompt del bot:
 * nombre, descripción, tabla de precios (obligatoria), variantes que hay que preguntar, reglas,
 * preguntas frecuentes, fotos y los anuncios de Meta que lo promocionan. Cada bloque es una
 * sección con su título y su ayuda, para que el formulario se pueda recorrer en pantalla pequeña.
 *
 * Detalles del contrato que se respetan a propósito (ver `products-payload.ts`):
 *  - al CREAR los bloques vacíos se omiten del body (el backend aplica sus valores por defecto);
 *  - al EDITAR los bloques vacíos viajan como `null`, que es la única forma de vaciarlos;
 *  - `tablaPrecios` nunca se puede vaciar: el schema exige al menos un rango.
 */

export interface ProductDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` = crear; con producto = editar ese producto. */
  product: Product | null;
}

/** Bloque del formulario: título, ayuda, contenido y el error del bloque (si lo hay). */
function Section({
  title,
  hint,
  error,
  children,
}: {
  title: string;
  hint: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-panel border border-line p-4">
      <div className="space-y-0.5">
        <h3 className="text-body font-medium text-ink">{title}</h3>
        <p className="text-caption text-ink-muted">{hint}</p>
      </div>

      {children}

      {error ? <p className="text-micro text-danger-strong">{error}</p> : null}
    </section>
  );
}

export function ProductDialog({ open, onClose, product }: ProductDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const createProduct = useCreateProduct(tenantId);
  const updateProduct = useUpdateProduct(tenantId);

  const [form, setForm] = useState<ProductFormState>(emptyProductForm);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  /** El negocio pidió repartir el stock por COMBINACIÓN (la matriz) aunque todavía no tenga datos. */
  const [pedirMatriz, setPedirMatriz] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /** Cada apertura arranca limpia (o con los datos del producto que se va a editar). */
  useEffect(() => {
    if (!open) return;

    setForm(productFormStateFrom(product));
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setPedirMatriz(false);
  }, [open, product]);

  const saving = createProduct.isPending || updateProduct.isPending;
  const isEditing = product !== null;

  /**
   * Combinaciones posibles de las variantes (Talla × Color × …) para repartir el inventario. Se pide UNA
   * combinación de más que el tope para poder avisar de que la tabla quedó cortada.
   */
  const combinaciones = useMemo(
    () => combinacionesVariantes(form.datosParaOrden, MAX_COMBINACIONES + 1),
    [form.datosParaOrden],
  );
  const combinacionesTruncadas = combinaciones.length > MAX_COMBINACIONES;
  const combos = combinacionesTruncadas ? combinaciones.slice(0, MAX_COMBINACIONES) : combinaciones;

  /**
   * Variantes que se pueden usar (nombre + al menos una opción). La cantidad de cada valor va al lado de
   * su opción (bloque «Datos para la orden»); la matriz por **combinación** (talla 42 · negro) solo se
   * muestra cuando el producto ya tiene stock de ese tipo o cuando el negocio pide repartirlo así.
   */
  const ejes = useMemo(() => ejesConOpciones(form.datosParaOrden), [form.datosParaOrden]);
  const stockPorCombinacion = hayStockPorCombinacion(form.inventarioVariantes);
  const mostrarMatriz = stockPorCombinacion || pedirMatriz;

  /**
   * Invitación a repartir el stock por combinación: solo tiene sentido cuando hay VARIOS ejes que sí se
   * cruzan (talla × color). Con un solo eje cada opción ya lleva su cantidad, y con dos ejes de TALLA
   * («Talla Caballero» y «Talla Dama») el cruce no existe, así que ni se ofrece.
   */
  const invitarMatriz =
    ejes.length > 1 && !mostrarMatriz && !todosLosEjesSonDeTalla(form.datosParaOrden);

  /** Cambia un campo del formulario sin tocar los demás (los bloques de lista van completos). */
  function patch(changes: Partial<ProductFormState>) {
    setForm((current) => ({ ...current, ...changes }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validateProductForm(form);
    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      if (isEditing && product) {
        await updateProduct.mutateAsync({
          productId: product.id,
          payload: updateProductPayload(form),
        });
      } else {
        await createProduct.mutateAsync(createProductPayload(form));
      }

      onClose();
    } catch (error) {
      // El backend valida lo mismo (y más): sus mensajes se reparten entre el campo que los
      // produjo y el aviso general del formulario.
      setErrors(formErrorsFromApiError(error, PRODUCT_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Editar producto' : 'Nuevo producto'}
      description="Todo esto es lo que el bot puede decir de este producto: si un dato falta aquí, lo va a improvisar en la conversación."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {/* `form` enlaza este botón del pie con el formulario del cuerpo del modal. */}
          <Button type="submit" form="product-form" loading={saving}>
            {isEditing ? 'Guardar cambios' : 'Crear producto'}
          </Button>
        </>
      }
    >
      <form id="product-form" onSubmit={handleSubmit} noValidate className="space-y-5">
        <FormErrorAlert message={submitError} errors={errors} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Nombre"
            htmlFor="productNombre"
            required
            hint="Como lo nombra un cliente en el chat."
            error={fieldError(errors, 'nombre')}
          >
            <Input
              id="productNombre"
              name="nombre"
              placeholder="Ej. Collar antipulgas"
              autoFocus
              maxLength={PRODUCT_LIMITS.nombre}
              value={form.nombre}
              invalid={Boolean(fieldError(errors, 'nombre'))}
              onChange={(event) => patch({ nombre: event.target.value })}
            />
          </Field>

          <Field
            label="Inventario general"
            htmlFor="productInventario"
            hint="Unidades totales del producto. Deja vacío si no controlas unidades. Si el producto se vende por talla/color, repartí el stock en sus variantes; este campo queda como respaldo."
            error={fieldError(errors, 'inventario')}
          >
            <Input
              id="productInventario"
              name="inventario"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              placeholder="Sin control"
              value={form.inventario}
              invalid={Boolean(fieldError(errors, 'inventario'))}
              onChange={(event) => patch({ inventario: event.target.value })}
            />
          </Field>

          <Field
            label="Marca"
            htmlFor="productMarca"
            hint="Opcional. El bot la usa para responder «¿manejas Nike?». Una marca por producto."
            error={fieldError(errors, 'marca')}
          >
            <Input
              id="productMarca"
              name="marca"
              placeholder="Ej. Nike"
              maxLength={80}
              value={form.marca}
              invalid={Boolean(fieldError(errors, 'marca'))}
              onChange={(event) => patch({ marca: event.target.value })}
            />
          </Field>

          <Field
            label="Categoría"
            htmlFor="productCategoria"
            hint="Opcional. Agrupa el catálogo (ej. Zapatillas, Ropa, Accesorios)."
            error={fieldError(errors, 'categoria')}
          >
            <Input
              id="productCategoria"
              name="categoria"
              placeholder="Ej. Zapatillas"
              maxLength={80}
              value={form.categoria}
              invalid={Boolean(fieldError(errors, 'categoria'))}
              onChange={(event) => patch({ categoria: event.target.value })}
            />
          </Field>
        </div>

        <Field
          label="Descripción"
          htmlFor="productDescripcion"
          hint="Materiales, medidas, beneficios… lo que el bot usa para argumentar."
          error={fieldError(errors, 'descripcion')}
        >
          <Textarea
            id="productDescripcion"
            name="descripcion"
            rows={3}
            value={form.descripcion}
            invalid={Boolean(fieldError(errors, 'descripcion'))}
            onChange={(event) => patch({ descripcion: event.target.value })}
          />
        </Field>

        <Section
          title="Tabla de precios por cantidad"
          hint="El bot vende con estos números. Ej. 1 unidad a 25.000 y desde 6 a 22.000 cada una."
          error={fieldError(errors, 'tablaPrecios')}
        >
          <PriceTierFields
            rows={form.tablaPrecios}
            disabled={saving}
            onChange={(tablaPrecios) => patch({ tablaPrecios })}
          />
        </Section>

        <Section
          title="Datos para la orden"
          hint="Lo que el bot debe preguntar antes de armar el pedido (talla, color…). El orden en que aparecen es el orden en que los pregunta, y cada opción lleva su cantidad al lado: el bot descuenta la que se venda y no ofrece las que estén en 0 (una cantidad vacía = sin control de stock)."
          error={fieldError(errors, 'datosParaOrden') ?? fieldError(errors, 'inventarioVariantes')}
        >
          <OrderVariantFields
            rows={form.datosParaOrden}
            disabled={saving}
            onChange={(datosParaOrden) => patch({ datosParaOrden })}
            inventario={{
              values: form.inventarioVariantes,
              onChange: (inventarioVariantes) => patch({ inventarioVariantes }),
            }}
          />

          {invitarMatriz ? (
            <p className="mt-3 text-caption text-ink-muted">
              ¿Este producto lleva stock por el CRUCE de sus variantes (ej. talla 42 · negro, y no una
              cantidad por talla)?{' '}
              <button
                type="button"
                className="text-brand-600 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                disabled={saving}
                onClick={() => setPedirMatriz(true)}
              >
                Repartirlo por combinación
              </button>
            </p>
          ) : null}
        </Section>

        {/* La matriz por COMBINACIÓN solo aparece si el producto ya tiene stock de ese tipo (el cruce es
            más específico que la cantidad por variante) o si el negocio pidió repartirlo así. */}
        {mostrarMatriz ? (
          <Section
            title="Inventario por combinación"
            hint="Una cantidad por CRUCE de variantes (talla 42 · negro). Es más específico que la cantidad por variante de arriba: si el cliente pide esa combinación, el bot descuenta esta. Déjala vacía (o «Vaciar todas») si el cruce no tiene control de stock: las cantidades de arriba no se tocan."
          >
            <VariantInventoryFields
              combos={combos}
              values={form.inventarioVariantes}
              disabled={saving}
              truncada={combinacionesTruncadas}
              onChange={(inventarioVariantes) => patch({ inventarioVariantes })}
            />
          </Section>
        ) : null}

        <Section
          title="Reglas del producto"
          hint="Condiciones que el bot debe respetar: combos, obsequios, promociones. Ej. «desde 3 unidades, envío gratis»."
          error={fieldError(errors, 'reglas')}
        >
          <StringListFields
            values={form.reglas}
            disabled={saving}
            itemLabel="Regla"
            placeholder="Ej. Desde 3 unidades, el envío es gratis"
            addLabel="Agregar regla"
            onChange={(reglas) => patch({ reglas })}
          />
        </Section>

        <Section
          title="Preguntas frecuentes"
          hint="La respuesta exacta que debe dar el bot cuando le preguntan esto (no improvisa). Sirven para las dos etapas: antes de comprar (envíos, materiales, garantía) y después (cómo se usa, cada cuánto, cuidados, conservación), porque el asistente de postventa recibe las de los productos que el cliente ya compró."
          error={fieldError(errors, 'faqs')}
        >
          <FaqFields rows={form.faqs} disabled={saving} onChange={(faqs) => patch({ faqs })} />
        </Section>

        <Section
          title="Fotos del producto"
          hint="Sube las fotos desde tu computador o pega la dirección (URL) de una imagen que ya esté publicada. Hasta 20: el bot las usa para reconocer el producto cuando el cliente manda una foto."
          error={fieldError(errors, 'fotos')}
        >
          <ProductPhotoFields
            values={form.fotos}
            disabled={saving}
            onChange={(fotos) => patch({ fotos })}
          />
        </Section>

        <Section
          title="Fotos por color"
          hint="Una foto por color: el bot manda la del color que el cliente pida y sirve para todas las tallas de ese color. Si pide fotos sin decir color, manda una de cada uno. Un color sin foto propia usa las generales de arriba."
          error={fieldError(errors, 'fotosVariantes')}
        >
          <VariantPhotoFields
            datosParaOrden={form.datosParaOrden}
            values={form.fotosVariantes}
            disabled={saving}
            onChange={(fotosVariantes) => patch({ fotosVariantes })}
          />
        </Section>

        <Section
          title="Anuncios de Meta"
          hint="IDs de los anuncios que promocionan este producto (vienen en los referrals del webhook). Sirven para saber por qué anuncio llegó la venta."
          error={fieldError(errors, 'metaAdIds')}
        >
          <StringListFields
            values={form.metaAdIds}
            disabled={saving}
            itemLabel="ID de anuncio"
            placeholder="Ej. 120210123456789"
            addLabel="Vincular anuncio"
            onChange={(metaAdIds) => patch({ metaAdIds })}
          />
        </Section>

        <div className="flex items-center justify-between gap-4 rounded-panel border border-line bg-surface-muted/50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-body font-medium text-ink">Producto activo</p>
            <p className="text-caption text-ink-muted">
              Solo los productos activos entran al prompt del bot. Desactívalo si está agotado o
              fuera de temporada en vez de borrarlo: se conserva con todo lo que ya configuraste.
            </p>
          </div>

          <Switch
            checked={form.activo}
            label="Producto activo"
            disabled={saving}
            onChange={(activo) => patch({ activo })}
          />
        </div>
      </form>
    </Modal>
  );
}
